import { Box, Stack } from '@mui/material';
import {
  Contact,
  ContactSubtitle,
  Layout,
} from '@rumblefish/ui/Rumblefish23Theme';
import { ContactTitle } from '@rumblefish/ui/src/components/Rumblefish23Theme/Contact/styles';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { NextHeadWithMeta } from '../NextHeadWithMeta';
import { Benefits } from './Benefits/Benefits';
import { Faq } from './Faq';
import { Hero } from './Hero';
import { PanenkaBanner } from './PanenkaBanner';
import { PanenkaCaseStudy } from './PanenkaCaseStudy/PanenkaCaseStudy';
import { syntheticDataGenerationContactFormId } from './syntheticDataGenerationPage.consts';
import { syntheticDataGenerationPageSeo } from './syntheticDataGenerationPage.seo';
import type { SyntheticDataGenerationPageProps } from './syntheticDataGenerationPage.types';
import { UseCases } from './UseCases/UseCases';
import { WhoThisIsFor } from './WhoThisIsFor/WhoThisIsFor';
import { WhyChooseRF } from './WhyChooseRF/WhyChooseRF';
import { WhyDoYouNeed } from './WhyDoYouNeed/WhyDoYouNeed';
import { WhyIsItBetter } from './WhyIsItBetter/WhyIsItBetter';
import { OurWork } from './OurWork';

export const SyntheticDataGenerationPage = ({
  blogPosts,
  caseStudies,
}: SyntheticDataGenerationPageProps) => {
  const [panenkaCaseStudyRef, inView] = useInView({
    threshold: 0.3,
  });
  const [showPanenkaBanner, setShowPanenkaBanner] = useState(false);

  useEffect(() => {
    if (inView) {
      setShowPanenkaBanner(true);
    } else {
      setShowPanenkaBanner(false);
    }
  }, [inView]);

  return (
    <Layout blogPosts={blogPosts}>
      <NextHeadWithMeta {...syntheticDataGenerationPageSeo} />
      <Hero />
      <WhyDoYouNeed />
      <Benefits />
      <UseCases />
      <WhyIsItBetter />
      <WhyChooseRF />
      <Box ref={panenkaCaseStudyRef} position="relative">
        <PanenkaBanner show={showPanenkaBanner} />

        <PanenkaCaseStudy />
      </Box>
      <WhoThisIsFor />
      <OurWork caseStudies={caseStudies} />
      <Faq />
      <Contact
        color="aquamarine"
        title="Your Product Deserves Custom Synthetic Data Engineering"
        id={syntheticDataGenerationContactFormId}
        titleComponent={
          <>
            <ContactTitle variant="display_sm">
              Your Product Deserves Custom Synthetic Data Engineering
            </ContactTitle>
          </>
        }
        subtitleComponent={
          <Stack gap={2}>
            <ContactSubtitle variant="regular_500">
              Whether you're building the next innovative game, training
              specialized AI models, or solving unique product challenges,
              generic platforms won't cut it. We’ve got you covered.
            </ContactSubtitle>

            <ContactSubtitle variant="regular_500">
              From ideation to deployment, Rumble Fish takes full ownership of
              your synthetic data challenges. Battle-tested technology, true
              partnership, and the expertise to solve problems that platforms
              can't touch.
            </ContactSubtitle>
          </Stack>
        }
      />
    </Layout>
  );
};
